/**
 * ReadBackForm — captures the VAL-2 read-back that CLOSES a D-29 escalation. The loop closes only on
 * an active attestation (the prescriber's verbatim restatement, or staff transcription of a phone
 * read-back) — a bare delivery receipt is NOT sufficient. The read-back text is required client-side
 * (the server also refuses an empty one). Gated by the caller on `validation.valeurcritique.acknowledge`.
 */
import { useState } from "react";
import { Button, FormField, Input, Textarea } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";

export interface ReadBackFormProps {
  onSubmit: (input: { read_back_text: string; acquitte_par?: string }) => Promise<void>;
  submitting?: boolean;
  disabled?: boolean;
}

export function ReadBackForm({ onSubmit, submitting, disabled }: ReadBackFormProps) {
  const { t } = useT();
  const [readBack, setReadBack] = useState("");
  const [by, setBy] = useState("");
  const [touched, setTouched] = useState(false);

  const invalid = readBack.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (invalid || submitting || disabled) return;
    await onSubmit({
      read_back_text: readBack.trim(),
      ...(by.trim() ? { acquitte_par: by.trim() } : {}),
    });
    setReadBack("");
    setBy("");
    setTouched(false);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      style={{ display: "flex", flexDirection: "column", gap: token.s3 }}
    >
      <p style={{ margin: 0, color: token.slate, fontSize: token.fsLabel }}>{t("validation.ack.desc")}</p>
      <FormField
        label={t("validation.ack.readBack")}
        hint={t("validation.ack.readBack.hint")}
        required
        error={touched && invalid ? t("validation.ack.required") : undefined}
      >
        {(p) => (
          <Textarea
            {...p}
            value={readBack}
            onChange={(e) => setReadBack(e.target.value)}
            invalid={touched && invalid}
            rows={3}
            disabled={disabled}
          />
        )}
      </FormField>
      <FormField label={t("validation.ack.by")} hint={t("validation.ack.by.hint")}>
        {(p) => (
          <Input {...p} value={by} onChange={(e) => setBy(e.target.value)} disabled={disabled} />
        )}
      </FormField>
      <Button type="submit" loading={submitting === true} disabled={disabled || invalid}>
        {t("validation.ack.submit")}
      </Button>
    </form>
  );
}
